import { useEffect } from 'react'

import { SITE_ORIGIN, articleSchema, breadcrumbSchema } from '@/seo/schema'

const DEFAULT_DESCRIPTION =
  'Finhudas — apskaitos programa Bolt ir Uber pavežėjams. Automatinis pajamų importas, mokesčių skaičiavimas ir deklaracijų pildymas.'

type JsonLd = Record<string, unknown>

interface PageSetupOptions {
  scroll?: boolean
  description?: string
  /** Path (e.g. "/blog/foo") or absolute URL. Defaults to the current pathname. */
  canonical?: string
  /** Article image (path or absolute URL) for BlogPosting JSON-LD. Omitted when not provided. */
  image?: string
  type?: 'website' | 'article'
  /** ISO date (YYYY-MM-DD). Builds Article JSON-LD when type === 'article'. */
  datePublished?: string
  /** Keep auth/app pages out of the search index. */
  noindex?: boolean
  /** Explicit JSON-LD. Overrides the automatic Article/Breadcrumb for articles. */
  jsonLd?: JsonLd | JsonLd[]
}

const toAbsoluteUrl = (value: string) => {
  if (/^https?:\/\//.test(value)) return value
  return `${SITE_ORIGIN}${value.startsWith('/') ? '' : '/'}${value}`
}

const toCanonical = (path?: string) => {
  const raw = path ?? window.location.pathname
  if (/^https?:\/\//.test(raw)) return raw
  const withSlash = raw.startsWith('/') ? raw : `/${raw}`
  const clean =
    withSlash !== '/' && withSlash.endsWith('/') ? withSlash.slice(0, -1) : withSlash
  return `${SITE_ORIGIN}${clean}`
}

const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

const JSON_LD_ID = 'seo-jsonld'
const setJsonLd = (serialized: string) => {
  let el = document.getElementById(JSON_LD_ID)
  if (!serialized) {
    if (el) el.remove()
    return
  }
  if (!el) {
    el = document.createElement('script')
    el.id = JSON_LD_ID
    el.setAttribute('type', 'application/ld+json')
    document.head.appendChild(el)
  }
  el.textContent = serialized
}

/**
 * Sets the document title, meta description, canonical, robots directive and JSON-LD
 * for the current page. Runs on mount and whenever the inputs change. Note: meta is
 * applied client-side (post-hydration), so non-JS crawlers fall back to the defaults
 * baked into index.html. (Open Graph / Twitter tags are intentionally omitted until
 * social meta is localized.)
 */
export const usePageSetup = (title: string, options: PageSetupOptions = {}) => {
  const { scroll, description, canonical, image, type, datePublished, noindex, jsonLd } = options
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : ''

  useEffect(() => {
    const desc = description || DEFAULT_DESCRIPTION
    const url = toCanonical(canonical)
    const articleImage = image ? toAbsoluteUrl(image) : undefined
    const pageType = type || 'website'

    document.title = title
    if (scroll) {
      document.querySelector('#root')?.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }

    upsertMeta('name', 'description', desc)
    upsertLink('canonical', url)
    // Only emit robots when there's something to say. `index, follow` is the crawler
    // default, so we remove the tag on indexable pages (also clears a stale `noindex`
    // left over from a previously-visited auth page during SPA navigation).
    if (noindex) {
      upsertMeta('name', 'robots', 'noindex, nofollow')
    } else {
      document.head.querySelector('meta[name="robots"]')?.remove()
    }

    let ld = jsonLdKey
    if (!ld && pageType === 'article') {
      ld = JSON.stringify([
        articleSchema({ title, description: desc, url, datePublished, image: articleImage }),
        breadcrumbSchema([
          { name: 'Pradžia', url: `${SITE_ORIGIN}/` },
          { name: 'Blogas', url: `${SITE_ORIGIN}/blog` },
          { name: title, url },
        ]),
      ])
    }
    setJsonLd(ld)
  }, [title, scroll, description, canonical, image, type, datePublished, noindex, jsonLdKey])
}
